import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, Alert, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Slider, ButtonGroup } from "@mui/material";
import SettingsIcon from '@mui/icons-material/Settings';
import classes from "../../styling/components/PhishingDemoView.module.css";
import { useTranslation } from "react-i18next";
import { PhishingDemoResponse, PromptSettings, PhishingDemoRequest } from "../../model/PhishingDemo";
import { generatePhishingMail, updatePromptSettings, getPromptSettings } from "../../api/phishingDemoClient";
import { PhishingMailPreview } from "./PhishingMailPreview";

interface FormData {
    name: string;
    company: string;
    topic: string;
}

type TopicCTA = 'default' | 'linkedin' | 'ruhrsummit' | 'custom';

const TOPIC_CONTENT = {
    default: "Choose the topic best suited to make the mail believable.",
    linkedin: "Choose a topic based on information you found on this user's LinkedIn profile.",
    ruhrsummit: "This user is attending ruhrSUMMIT convention in Bochum right now. Search the web for the convention. Use this context to craft a believable message, such as event logistics, after-parties, or networking follow-up.",
    custom: ""
};

export const PhishingDemoView: React.FC = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<FormData>({
        name: '',
        company: '',
        topic: ''
    });
    const [selectedTopicCTA, setSelectedTopicCTA] = useState<TopicCTA>('default');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorStates, setErrorStates] = useState<Record<string, boolean>>({});
    const [hasError, setHasError] = useState<boolean>(false);
    const [generatedMail, setGeneratedMail] = useState<PhishingDemoResponse | null>(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [promptSettings, setPromptSettings] = useState<PromptSettings | null>(null);
    const [editedPrompts, setEditedPrompts] = useState<PromptSettings | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoadingPrompts, setIsLoadingPrompts] = useState(true);

    useEffect(() => {
        const loadPrompts = async () => {
            try {
                const response = await getPromptSettings();
                if (response.ok) {
                    const settings = await response.json();
                    setPromptSettings(settings);
                    setEditedPrompts(settings);
                }
            } catch (error) {
                console.warn('Error loading prompt settings:', error);
            } finally {
                setIsLoadingPrompts(false);
            }
        };

        loadPrompts();
    }, [t]);

    const handleTextFieldError = (name: string, errorState: boolean) => {
        setErrorStates(prevErrorStates => {
            const updatedErrorStates = {
                ...prevErrorStates,
                [name]: errorState,
            };
            const anyError = Object.values(updatedErrorStates).some(error => error);
            setHasError(anyError);
            return updatedErrorStates;
        });
    };

    const validateField = (name: string, value: string): boolean => {
        if (!value.trim()) {
            return true; // Error if empty
        }
        return false;
    };

    const handleChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        const hasError = validateField(field, value);
        handleTextFieldError(field, hasError);
    };

    const handleTopicCTASelect = (cta: TopicCTA) => {
        setSelectedTopicCTA(cta);
        if (cta !== 'custom') {
            setFormData(prev => ({
                ...prev,
                topic: ''
            }));
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setIsSubmitting(true);
        setGeneratedMail(null);

        try {
            const topicContent = selectedTopicCTA === 'custom' 
                ? formData.topic 
                : TOPIC_CONTENT[selectedTopicCTA];

            const request: PhishingDemoRequest = {
                name: formData.name,
                company: formData.company,
                topic: topicContent
            };
            const response = await generatePhishingMail(request);

            if (!response.ok) {
                throw new Error('Failed to generate phishing mail');
            }

            const result = await response.json();
            setGeneratedMail(result);
        } catch (error) {
            setError(t('phishingDemo.error', 'Failed to generate phishing mail. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSettingsOpen = () => {
        setIsSettingsOpen(true);
    };

    const handleSettingsClose = () => {
        setIsSettingsOpen(false);
    };

    const handlePromptChange = (field: keyof PromptSettings) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (editedPrompts) {
            setEditedPrompts({
                ...editedPrompts,
                [field]: event.target.value
            });
        }
    };

    const handleEditStart = () => {
        setEditedPrompts({
            systemPrompt: promptSettings?.systemPrompt ?? '',
            userPrompt: promptSettings?.userPrompt ?? '',
            isActive: true,
            temperature: promptSettings?.temperature ?? 0.7
        });
        setIsEditing(true);
    };

    const handleEditCancel = () => {
        setEditedPrompts(promptSettings ?? {
            systemPrompt: '',
            userPrompt: '',
            isActive: true,
            temperature: 0.7
        });
        setIsEditing(false);
    };

    const handleEditSave = async () => {
        if (!editedPrompts) return;
        
        try {
            const response = await updatePromptSettings(editedPrompts);
            if (!response.ok) {
                throw new Error('Failed to save prompt settings');
            }
            const updatedSettings = await response.json();
            setPromptSettings(updatedSettings);
            setIsEditing(false);
        } catch (error) {
            setError(t('phishingDemo.promptSaveError', 'Failed to save prompt settings. Please try again.'));
        }
    };

    const handleResetPrompts = async () => {
        if (!promptSettings) return;
        
        try {
            const response = await updatePromptSettings(promptSettings);
            if (!response.ok) {
                throw new Error('Failed to reset prompt settings');
            }
            const resetSettings = await response.json();
            setPromptSettings(resetSettings);
            setEditedPrompts(resetSettings);
        } catch (error) {
            setError(t('phishingDemo.promptResetError', 'Failed to reset prompt settings. Please try again.'));
        }
    };

    const handleLogoClick = () => {
        setFormData({
            name: '',
            company: '',
            topic: ''
        });
        setSelectedTopicCTA('default');
        setGeneratedMail(null);
        setError(null);
        setErrorStates({});
        setHasError(false);
        setIsSubmitting(false);
    };

    if (isLoadingPrompts) {
        return (
            <Box className={classes.container} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress sx={{ color: 'var(--look2x-lilac)' }} />
            </Box>
        );
    }

    return (
        <Box className={classes.container}>
            <IconButton 
                onClick={handleSettingsOpen}
                className={classes.settingsButton}
                sx={{ 
                    position: 'absolute',
                    top: '2rem',
                    right: '2rem',
                    color: 'var(--look2x-lilac)',
                    '&:hover': {
                        backgroundColor: 'rgba(160, 174, 217, 0.1)'
                    }
                }}
            >
                <SettingsIcon />
            </IconButton>
            
            <img 
                src="/assets/img/look2x-logo-farbig-weiss.png" 
                alt="look2x Logo" 
                className={classes.logo}
                onClick={handleLogoClick}
            />
            <div className={classes.content}>
                <Typography variant="h1" className={classes.title}>
                    {t('phishingDemo.title', 'Can You Phish?')}
                </Typography>
                
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                
                {!generatedMail ? (
                    <form onSubmit={handleSubmit} className={classes.form} noValidate autoComplete="off">
                        <Typography variant="h2" className={classes.formTitle}>
                            {t('phishingDemo.formTitle', 'Target Information')}
                        </Typography>
                        <Typography variant="body1" className={classes.formSubtitle}>
                            {t('phishingDemo.formSubtitle', 'Enter the details of your target to generate a personalized phishing email')}
                        </Typography>
                        
                        <Typography variant="body2" className={classes.fieldTitle}>
                            {t('phishingDemo.nameTitle', 'Name')}
                        </Typography>
                        <TextField
                            className={classes.formField}
                            fullWidth
                            name="name"
                            value={formData.name}
                            placeholder={t('phishingDemo.namePlaceholder', 'Max Mustermann')}
                            variant="outlined"
                            onChange={handleChange('name')}
                            error={errorStates.name}
                            helperText={errorStates.name ? t('phishingDemo.required', 'This field is required') : ''}
                            disabled={isSubmitting}
                        />
                        
                        <Typography variant="body2" className={classes.fieldTitle}>
                            {t('phishingDemo.companyTitle', 'Company')}
                        </Typography>
                        <TextField
                            className={classes.formField}
                            fullWidth
                            name="company"
                            value={formData.company}
                            placeholder={t('phishingDemo.companyPlaceholder', 'Musterfirma GmbH')}
                            variant="outlined"
                            onChange={handleChange('company')}
                            error={errorStates.company}
                            helperText={errorStates.company ? t('phishingDemo.required', 'This field is required') : ''}
                            disabled={isSubmitting}
                        />

                        <Typography variant="body2" className={classes.fieldTitle}>
                            {t('phishingDemo.topicTitle', 'Thema')}
                        </Typography>
                        <ButtonGroup 
                            variant="outlined" 
                            className={`${classes.formField} ${classes.topicButtonGroup}`}
                            sx={{
                                display: 'block',
                                textAlign: 'left',
                                '& .MuiButton-root': {
                                    color: '#ffffff',
                                    borderColor: 'var(--look2x-lilac)',
                                    '&:hover': {
                                        borderColor: 'var(--look2x-lilac)',
                                        backgroundColor: 'rgba(160, 174, 217, 0.1)'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'var(--look2x-lilac)',
                                        color: 'var(--look2x-blue)',
                                        '&:hover': {
                                            backgroundColor: 'var(--look2x-lilac)',
                                            opacity: 0.9
                                        }
                                    }
                                }
                            }}
                        >
                            <Button
                                onClick={() => handleTopicCTASelect('default')}
                                variant={selectedTopicCTA === 'default' ? "contained" : "outlined"}
                            >
                                Default
                            </Button>
                            <Button
                                onClick={() => handleTopicCTASelect('linkedin')}
                                variant={selectedTopicCTA === 'linkedin' ? "contained" : "outlined"}
                            >
                                LinkedIn
                            </Button>
                            <Button
                                onClick={() => handleTopicCTASelect('ruhrsummit')}
                                variant={selectedTopicCTA === 'ruhrsummit' ? "contained" : "outlined"}
                            >
                                ruhrSUMMIT
                            </Button>
                            <Button
                                onClick={() => handleTopicCTASelect('custom')}
                                variant={selectedTopicCTA === 'custom' ? "contained" : "outlined"}
                            >
                                Custom
                            </Button>
                        </ButtonGroup>

                        {selectedTopicCTA === 'custom' && (
                            <TextField
                                className={classes.formField}
                                fullWidth
                                name="topic"
                                value={formData.topic}
                                placeholder={t('phishingDemo.topicPlaceholder', 'Enter topic...')}
                                variant="outlined"
                                onChange={handleChange('topic')}
                                error={errorStates.topic}
                                helperText={errorStates.topic ? t('phishingDemo.required', 'This field is required') : ''}
                                disabled={isSubmitting}
                            />
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            className={classes.generateButton}
                            disabled={isSubmitting || hasError || (selectedTopicCTA === 'custom' && !formData.topic)}
                        >
                            {isSubmitting 
                                ? t('phishingDemo.generating', 'Generating...') 
                                : t('phishingDemo.generate', 'Generate Phishing Mail')}
                        </Button>
                    </form>
                ) : (
                    <PhishingMailPreview
                        subject={generatedMail.subject}
                        body={generatedMail.body}
                        sender={generatedMail.sender}
                    />
                )}
            </div>

            <Dialog 
                open={isSettingsOpen} 
                onClose={handleSettingsClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'var(--look2x-blue)',
                        color: '#ffffff',
                        border: '1px solid var(--look2x-lilac)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    color: 'var(--look2x-lilac)',
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    {t('phishingDemo.settings', 'Settings')}
                    {!isEditing && (
                        <Button
                            onClick={handleEditStart}
                            sx={{
                                color: 'var(--look2x-lilac)',
                                '&:hover': {
                                    backgroundColor: 'rgba(160, 174, 217, 0.1)'
                                }
                            }}
                        >
                            {t('common.edit', 'Edit')}
                        </Button>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="h6" sx={{ color: '#ffffff', mt: 2, mb: 1 }}>
                        {t('phishingDemo.systemPrompt', 'System Prompt')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={isEditing ? editedPrompts?.systemPrompt ?? '' : promptSettings?.systemPrompt ?? ''}
                        onChange={handlePromptChange('systemPrompt')}
                        variant="outlined"
                        disabled={!isEditing}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: '#ffffff',
                                '& fieldset': {
                                    borderColor: 'var(--look2x-lilac)',
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: '#ffffff',
                            }
                        }}
                    />
                    <Typography variant="h6" sx={{ color: '#ffffff', mt: 3, mb: 1 }}>
                        {t('phishingDemo.userPrompt', 'User Prompt')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={16}
                        value={isEditing ? editedPrompts?.userPrompt ?? '' : promptSettings?.userPrompt ?? ''}
                        onChange={handlePromptChange('userPrompt')}
                        variant="outlined"
                        disabled={!isEditing}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: '#ffffff',
                                '& fieldset': {
                                    borderColor: 'var(--look2x-lilac)',
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: '#ffffff',
                            }
                        }}
                    />
                    <Typography variant="h6" sx={{ color: '#ffffff', mt: 3, mb: 1 }}>
                        {t('phishingDemo.temperature', 'Temperature')}
                    </Typography>
                    <Box sx={{ px: 2 }}>
                        <Slider
                            value={isEditing ? editedPrompts?.temperature ?? 0.7 : promptSettings?.temperature ?? 0.7}
                            onChange={(_, value) => {
                                if (isEditing && editedPrompts) {
                                    setEditedPrompts({
                                        ...editedPrompts,
                                        temperature: value as number
                                    });
                                }
                            }}
                            min={0}
                            max={1}
                            step={0.1}
                            disabled={!isEditing}
                            sx={{
                                color: 'var(--look2x-lilac)',
                                '& .MuiSlider-thumb': {
                                    '&:hover, &.Mui-focusVisible': {
                                        boxShadow: '0px 0px 0px 8px rgba(160, 174, 217, 0.16)'
                                    }
                                }
                            }}
                        />
                        <Typography variant="body2" sx={{ color: '#ffffff', textAlign: 'center', mt: 1 }}>
                            {isEditing ? editedPrompts?.temperature.toFixed(1) : promptSettings?.temperature.toFixed(1)}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                    {promptSettings && (
                        <Button 
                            onClick={handleResetPrompts}
                            sx={{
                                color: 'var(--look2x-lilac)',
                                '&:hover': {
                                    backgroundColor: 'rgba(160, 174, 217, 0.1)'
                                }
                            }}
                        >
                            {t('common.reset', 'Reset to Defaults')}
                        </Button>
                    )}
                    {!promptSettings && <Box />}
                    <Box>
                        {isEditing ? (
                            <>
                                <Button 
                                    onClick={handleEditCancel}
                                    sx={{
                                        color: 'var(--look2x-lilac)',
                                        mr: 1,
                                        '&:hover': {
                                            backgroundColor: 'rgba(160, 174, 217, 0.1)'
                                        }
                                    }}
                                >
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                                <Button 
                                    onClick={handleEditSave}
                                    variant="contained"
                                    sx={{
                                        backgroundColor: 'var(--look2x-lilac)',
                                        color: 'var(--look2x-blue)',
                                        '&:hover': {
                                            backgroundColor: '#8a97c4'
                                        }
                                    }}
                                >
                                    {t('common.save', 'Save')}
                                </Button>
                            </>
                        ) : (
                            <Button 
                                onClick={handleSettingsClose}
                                sx={{
                                    color: 'var(--look2x-lilac)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(160, 174, 217, 0.1)'
                                    }
                                }}
                            >
                                {t('common.close', 'Close')}
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
}; 